import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiShoppingCart, FiMessageSquare, FiX, FiCheckCircle, FiZap, FiBox } = FiIcons;

const mockProduct = {
  name: '"Aether" High-Top Sneakers',
  price: 185.00,
  image: 'https://images.unsplash.com/photo-1600185365926-3a2ce3cdb9eb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
};

const CartRecoverySimulation = () => {
  const [messages, setMessages] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  const script = [
    { from: 'ai', text: 'Hey! It looks like you left something in your cart. Need any help checking out?', delay: 2000 },
    { from: 'user', text: 'Oh, hey. Yeah, I was wondering about shipping times.', delay: 4000 },
    { from: 'ai', text: 'We offer free 2-day shipping on all orders over $100!', delay: 2000 },
    { from: 'ai', text: 'I can also offer you a 10% discount to complete your purchase today. Would you like the code?', delay: 3000 },
    { from: 'user', text: 'Wow, 10% off and free shipping? Sold. Yes, please!', delay: 4000 },
    { from: 'ai', text: 'Awesome! Here is your code: WELCOME10. I\'ve already applied it to your cart for you.', icon: FiZap, delay: 3000 },
    { from: 'system', text: 'Discount WELCOME10 Applied!', price: mockProduct.price * 0.9, delay: 1000 },
    { from: 'system', text: 'Order Confirmed! Thank you!', icon: FiCheckCircle, final: true, delay: 2000 },
  ];

  useEffect(() => {
    let timeout;
    if (isVisible) {
      timeout = setTimeout(() => {
        let cumulativeDelay = 0;
        script.forEach((message, index) => {
          cumulativeDelay += message.delay;
          setTimeout(() => {
            setMessages(prev => [...prev.slice(0, index), message]);
          }, cumulativeDelay);
        });
      }, 1000);
    } else {
      setMessages([]);
    }
    return () => clearTimeout(timeout);
  }, [isVisible]);

  useEffect(() => {
    // Trigger the animation on mount
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const getMessageComponent = (msg, index) => {
    if (msg.from === 'user') {
      return (
        <motion.div key={index} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} className="flex justify-end">
          <div className="bg-blue-500 text-white p-3 rounded-xl max-w-xs">
            <p className="text-sm">{msg.text}</p>
          </div>
        </motion.div>
      );
    }
    if (msg.from === 'ai') {
      return (
        <motion.div key={index} initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} className="flex gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 flex-shrink-0 flex items-center justify-center">
            <SafeIcon icon={FiMessageSquare} className="text-white h-4 w-4" />
          </div>
          <div className="bg-gray-700 p-3 rounded-xl max-w-xs">
            <p className="text-sm text-gray-200">{msg.text}</p>
          </div>
        </motion.div>
      );
    }
    if (msg.from === 'system') {
      return (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center text-sm py-2"
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${msg.final ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
            {msg.icon && <SafeIcon icon={msg.icon} />}
            <span>{msg.text}</span>
          </div>
        </motion.div>
      );
    }
  };

  const currentPrice = messages.find(m => m.price)?.price || mockProduct.price;
  const hasDiscount = messages.some(m => m.price);

  return (
    <div className="w-full max-w-md mx-auto bg-slate-800/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-slate-900/50 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 flex items-center justify-center">
            <SafeIcon icon={FiMessageSquare} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white">AI Sales Assistant</h3>
            <p className="text-xs text-emerald-400 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              Online
            </p>
          </div>
        </div>
        <button className="text-gray-400 hover:text-white">
          <SafeIcon icon={FiX} />
        </button>
      </div>

      {/* Cart Item */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 border-b border-white/10"
          >
            <div className="flex items-center gap-4">
              <img src={mockProduct.image} alt={mockProduct.name} className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-grow">
                <p className="font-semibold text-white">{mockProduct.name}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className={`font-bold text-lg ${hasDiscount ? 'text-emerald-400' : 'text-white'}`}>${currentPrice.toFixed(2)}</p>
                  {hasDiscount && <p className="text-sm text-gray-400 line-through">${mockProduct.price.toFixed(2)}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 text-white bg-slate-700 px-3 py-1 rounded-full">
                <SafeIcon icon={FiBox} className="text-sm" />
                <span>1</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Area */}
      <div className="p-4 h-64 overflow-y-auto space-y-4">
        <AnimatePresence initial={false}>
          {messages.map((msg, index) => getMessageComponent(msg, index))}
        </AnimatePresence>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-slate-900/50 border-t border-white/10">
        <div className="w-full bg-slate-700/50 border border-slate-600 rounded-lg h-10 flex items-center px-3">
          <p className="text-sm text-gray-500">Say something...</p>
        </div>
      </div>
    </div>
  );
};

export default CartRecoverySimulation;